import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import app from '@src/scripts/react-habitat/app';
import useInnerBlocks from '@src/scripts/util/useInnerBlocks';
import renderRichText from '@src/scripts/util/renderRichText';
import { useResponsive } from '@src/scripts/utils';

// eslint-disable-next-line import/no-unresolved
import * as TabsBlockStyles from '../TabsBlock/custom.styled.UNIVERSITY';

const TabsItemBlock = ( props ) => {
	const { title, isSelected } = props;
	const {
		ref,
		isVertical,
		isMobile,
		mobileHorizontal,
		portalTarget,
		index,
		selected,
		handleTabNav,
		children,
	} = useTabsData( props );

	//Selects the Tab if it is selected in the Gutenberg Editor
	useEffect( () => {
		if ( isSelected ) {
			handleTabNav();
		}
	}, [ isSelected ] );

	useEffect( () => {
		if ( portalTarget ) {
			app.update( portalTarget );
		}
	}, [ portalTarget ] );

	// console.log({selected, index}, selected === index) //eslint-disable-line

	return (
		<TabsBlockStyles.TabsItemWrapper
			ref={ ref }
			className={ 'dhsv-tabs--item' }
			//Initial open state
			data-open={ selected === index }
		>
			{ /* Tab Nav Button */ }
			<TabsBlockStyles.TabButton onClick={ handleTabNav }>
				{ renderRichText( title, 'span' ) }
				{ isVertical && isMobile && ! mobileHorizontal && (
					<span className="dhsv-button-icon">
						<span></span>
						<span></span>
					</span>
				) }
			</TabsBlockStyles.TabButton>
			{ /* Tab Content */ }
			{ portalTarget &&
				( isVertical && isMobile && ! mobileHorizontal
					? React.createElement(
							'div',
							{ className: 'tab-content-wrapper' },
							<TabItemInnerBlock
								{ ...{
									content: children,
									selected,
									index,
								} }
							/>
					  )
					: ReactDOM.createPortal(
							<TabItemInnerBlock
								{ ...{
									content: children,
									selected,
									index,
								} }
							/>,
							portalTarget
					  ) ) }
		</TabsBlockStyles.TabsItemWrapper>
	);
};

const TabItemInnerBlock = ( { content, selected, index } ) => {
	const ref = useRef();

	useEffect( () => {
		if ( ref.current ) {
			app.update( ref.current );
		}
	}, [ ref ] );

	return (
		<TabsBlockStyles.TabContent
			className={
				'tab-content' + ( selected === index ? ' active' : '' )
			}
			hidden={ selected === -1 }
			style={ { order: index } }
			ref={ ref }
		>
			{ renderRichText( content, 'div' ) }
		</TabsBlockStyles.TabContent>
	);
};

TabsItemBlock.propTypes = {
	isMobile: PropTypes.bool,
};

TabItemInnerBlock.propTypes = {
	content: PropTypes.any,
};

const useTabsData = ( { tabsCount = 0, children, isEditor = false } ) => {
	const { ref } = useInnerBlocks( children, 'div', {} );
	const [ tabsEl, setTabsEl ] = useState();
	const [ portalTarget, setPortalTarget ] = useState();
	const [ index, setIndex ] = useState( null );
	const [ selected, setSelected ] = useState( null );
	const resp = useResponsive();
	const isMobile = resp === 'mobile';
	const isVertical = tabsEl
		? tabsEl.classList.contains( 'is-vertical' )
		: false;
	const mobileHorizontal = tabsEl
		? tabsEl.classList.contains( 'mobile-horizontal' )
		: false;

	const handleTabNav = () => {
		setSelected( index );

		const tabItems = tabsEl
			? Array.from(
					tabsEl.querySelectorAll( '.tabs-nav .dhsv-tabs--item' )
			  )
			: [];

		const tabContents = tabsEl
			? Array.from( tabsEl.querySelectorAll( '.tabs-body .tab-content' ) )
			: [];

		tabContents.forEach( ( item, key ) => {
			item.classList.toggle( 'active', key === index );
		} );

		tabItems.forEach( ( item, key ) => {
			item.setAttribute( 'data-open', key === index );

			if ( isMobile && isVertical && ! mobileHorizontal ) {
				const tabContentWrapper = item?.querySelector(
					'.tab-content-wrapper'
				);
				const tabContent = item?.querySelector( '.tab-content' );

				if ( tabContentWrapper && tabContent ) {
					const contentHeight =
						key === index ? tabContent.clientHeight : 0;
					tabContentWrapper.style.height = `${ contentHeight }px`;
				}
			}
		} );
	};

	useEffect( () => {
		if ( ref.current ) {
			setTabsEl( () => {
				return ref.current?.closest( '.dhsv-tabs' );
			} );

			if ( isEditor ) {
				setIndex( () => {
					const parent = ref.current?.closest( '.tabs-nav' );
					const child = ref.current;
					return Array.prototype.indexOf.call(
						parent.children,
						child.parentNode
					);
				} );
			} else {
				setIndex(
					parseInt(
						ref.current
							.closest( '.tabItemContainer' )
							.getAttribute( 'data-tab-index' )
					)
				);
			}
		}
	}, [ ref, tabsCount ] );

	useEffect( () => {
		if ( tabsEl ) {
			setPortalTarget( tabsEl.querySelector( '.tabs-body' ) );
			setSelected( parseFloat( tabsEl.getAttribute( 'data-selected' ) ) );
		}
	}, [ tabsEl ] );

	return {
		ref,
		isVertical,
		isMobile,
		mobileHorizontal,
		tabsEl,
		portalTarget,
		index: index === null ? -99 : index,
		selected: selected === null ? -1 : selected,
		handleTabNav,
		children,
	};
};

export default TabsItemBlock;
